/**
 * Settings block for the hosted "Request a call" consent form.
 * Props: api (API.json), toast(message, type).
 * The link uses the API host (window.API_BASE) plus /api/public/consent-form/:slug;
 * the admin host does not proxy /api.
 */
const { useEffect, useState } = React;

function errorMessage(err, fallback) {
  const data = err && err.data;
  return (data && (data.message || data.detail)) || (err && err.message) || fallback;
}

function absoluteUrl(path) {
  const base = (typeof window !== 'undefined' && (window.API_BASE || (window.location && window.location.origin))) || '';
  return String(base).replace(/\/$/, '') + (path || '');
}

function embedSnippet(url) {
  return '<iframe src="' + url + '" title="Request a call" style="width:100%;max-width:480px;height:720px;border:0;"></iframe>';
}

export function ConsentFormSettings({ api, toast }) {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [displayName, setDisplayName] = useState('');

  function apply(data) {
    const next = data && data.form;
    setForm(next || null);
    setDisplayName(next && next.business_display_name ? next.business_display_name : '');
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api('GET', '/api/consent-forms').then((data) => {
      if (!cancelled) apply(data);
    }).catch((err) => {
      if (!cancelled) setError(errorMessage(err, 'Could not load the request form.'));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [api]);

  function notify(message, type) {
    if (toast) toast(message, type);
  }

  async function save(body, success) {
    setBusy(true);
    setError('');
    try {
      const data = await api('POST', '/api/consent-forms', body);
      apply(data);
      if (success) notify(success);
    } catch (err) {
      const message = errorMessage(err, 'Could not update the request form.');
      setError(message);
      notify(message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function copyText(text, label) {
    try {
      await navigator.clipboard.writeText(text);
      notify(label + ' copied');
    } catch (err) {
      notify('Select the text and copy it.', 'error');
    }
  }

  if (loading) {
    return (
      <section>
        <h3 className="section-heading">Request a call form</h3>
        <p className="muted">Loading…</p>
      </section>
    );
  }

  if (!form) {
    return (
      <section>
        <h3 className="section-heading">Request a call form</h3>
        <p className="field-help">{error || 'The request form is not available for this workspace.'}</p>
      </section>
    );
  }

  const url = absoluteUrl(form.public_path);
  const embed = embedSnippet(url);

  return (
    <section>
      <h3 className="section-heading">Request a call form</h3>
      <p className="field-help">
        Put this link on your website or ads, or embed it. The consent box starts unchecked.
        Someone who checks it and submits gives prior express written consent for calls and texts about their request, including AI voices.
      </p>
      {error ? <p className="field-help" role="alert">{error}</p> : null}
      <label className="field" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={!!form.enabled}
          disabled={busy}
          onChange={(event) => save({ enabled: event.target.checked }, event.target.checked ? 'Request form is on' : 'Request form is off')}
        />
        <span>Accept requests on this form</span>
      </label>
      {!form.enabled ? <p className="field-help">The public page is off until you turn this on.</p> : null}
      <div className="field">
        <label htmlFor="consent-form-link">Form link</label>
        <input id="consent-form-link" className="input" readOnly value={url} />
      </div>
      <button type="button" className="button small outline" disabled={busy} onClick={() => copyText(url, 'Link')}>Copy link</button>
      <div className="field" style={{ marginTop: 12 }}>
        <label htmlFor="consent-form-embed">Embed code</label>
        <textarea id="consent-form-embed" className="input" readOnly rows={3} value={embed} />
      </div>
      <button type="button" className="button small outline" disabled={busy} onClick={() => copyText(embed, 'Embed code')}>Copy embed code</button>
      <p className="field-help" style={{ marginTop: 8 }}>
        Add ?from=https://your-site.com/page to the link if you want that address stored with the consent.
      </p>
      <div className="field" style={{ marginTop: 12 }}>
        <label htmlFor="consent-form-business">Name in the consent sentence</label>
        <input
          id="consent-form-business"
          className="input"
          maxLength={80}
          value={displayName}
          disabled={busy}
          placeholder={form.business_name || 'Business name'}
          onChange={(event) => setDisplayName(event.target.value)}
        />
      </div>
      <button
        type="button"
        className="button small"
        disabled={busy}
        onClick={() => save({ business_display_name: displayName }, 'Consent name saved')}
      >
        Save name
      </button>
      <p className="field-help" style={{ marginTop: 12 }}>
        Consent text, version {form.consent_text_version}. This exact sentence is stored with each submission.
      </p>
      <blockquote style={{ margin: '8px 0 0', padding: '12px 14px', borderLeft: '3px solid var(--accent, #3b82f6)', lineHeight: 1.45 }}>
        {form.consent_text}
      </blockquote>
    </section>
  );
}
